import React, { useState, useEffect } from 'react';
import {
  Stack,
  Switch,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Cable as SSHIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Add as AddIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { sshAPI, agentsAPI } from '../services/api';

function SSHConfig() {
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  // SSH is a RETAINED FALLBACK and ships OFF -- domain controllers are reached
  // through agents. Surfacing that here, where the credentials live, stops
  // someone configuring SSH and wondering why nothing ever uses it.
  const [policy, setPolicy] = useState(null);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [enableReason, setEnableReason] = useState('');
  const sshEnabled = Boolean(policy?.ssh_fallback_active);

  // Dialog states
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Password visibility states
  const loadPolicy = React.useCallback(async () => {
    try {
      const r = await agentsAPI.getPolicy();
      setPolicy(r.data);
    } catch {
      setPolicy(null);
    }
  }, []);

  useEffect(() => {
    loadPolicy();
  }, [loadPolicy]);

  const toggleSsh = async (on) => {
    setPolicyBusy(true);
    setError('');
    try {
      await agentsAPI.setPolicy(
        on ? { enabled: true, reason: enableReason.trim(), ttl_seconds: 3600 } : { enabled: false }
      );
      setEnableReason('');
      await loadPolicy();
      setSuccess(on ? 'SSH fallback enabled.' : 'SSH fallback disabled.');
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Could not change the transport policy');
    } finally {
      setPolicyBusy(false);
    }
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showPassphrase, setShowPassphrase] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    host: '',
    port: 22,
    username: '',
    auth_method: 'password',
    password: '',
    private_key: '',
    passphrase: '',
    connection_timeout: 30,
  });

  useEffect(() => {
    loadSSHConfig();
  }, []);

  const loadSSHConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await sshAPI.getStatus();
      setIsConfigured(response.data.setup_completed || false);

      if (response.data.setup_completed) {
        const configResponse = await sshAPI.getConfig();
        setConfig(configResponse.data);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setIsConfigured(false);
      } else {
        setError(err.response?.data?.error || 'Failed to load SSH configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSetupDialog = (editMode = false) => {
    if (editMode && config) {
      setFormData({
        host: config.host || '',
        port: config.port || 22,
        username: config.username || '',
        auth_method: config.auth_method || 'password',
        password: '',
        private_key: '',
        passphrase: '',
        connection_timeout: config.connection_timeout || 30,
      });
    } else {
      setFormData({
        host: '',
        port: 22,
        username: '',
        auth_method: 'password',
        password: '',
        private_key: '',
        passphrase: '',
        connection_timeout: 30,
      });
    }
    setSetupDialogOpen(true);
  };

  const handleCloseSetupDialog = () => {
    setSetupDialogOpen(false);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async () => {
    if (!formData.host || !formData.username) {
      setError('Host and username are required');
      return;
    }

    if (formData.auth_method === 'password' && !formData.password && !config) {
      setError('Password is required for password authentication');
      return;
    }

    if ((formData.auth_method === 'key' || formData.auth_method === 'key_with_passphrase') && !formData.private_key && !config) {
      setError('Private key is required for key authentication');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const configData = {
        host: formData.host,
        port: formData.port,
        username: formData.username,
        auth_method: formData.auth_method,
        connection_timeout: formData.connection_timeout,
      };

      // Only include credentials if they were provided
      if (formData.auth_method === 'password' && formData.password) {
        configData.password = formData.password;
      } else if (formData.auth_method === 'key' && formData.private_key) {
        configData.private_key = formData.private_key;
      } else if (formData.auth_method === 'key_with_passphrase') {
        if (formData.private_key) configData.private_key = formData.private_key;
        if (formData.passphrase) configData.passphrase = formData.passphrase;
      }

      if (config) {
        // Update existing config
        await sshAPI.updateConfig(configData);
        setSuccess('SSH configuration updated successfully');
      } else {
        // Create new config
        await sshAPI.setupWizard(configData);
        setSuccess('SSH configuration created successfully');
      }

      handleCloseSetupDialog();
      loadSSHConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save SSH configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccess('');

    try {
      await sshAPI.testConnection();
      setSuccess('SSH connection test successful!');
    } catch (err) {
      setError(err.response?.data?.error || 'SSH connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      await sshAPI.deleteConfig();
      setSuccess('SSH configuration deleted successfully');
      setDeleteDialogOpen(false);
      setIsConfigured(false);
      setConfig(null);
      loadSSHConfig();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete SSH configuration');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !config) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Typography variant="h4">SSH Configuration</Typography>
        {isConfigured && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            disabled={!sshEnabled}
            onClick={() => handleOpenSetupDialog(true)}
          >
            Edit Configuration
          </Button>
        )}
      </Box>

      {/* Transport state. Everything below is greyed out while the fallback is
          off: editing credentials for a transport nothing uses wastes time. */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: sshEnabled ? 'warning.main' : 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControlLabel
            sx={{ mr: 0, flexGrow: 1 }}
            control={
              <Switch
                checked={sshEnabled}
                color="warning"
                disabled={policyBusy || (!sshEnabled && enableReason.trim().length < 10)}
                onChange={(e) => toggleSsh(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2">
                  SSH fallback is {sshEnabled ? 'ENABLED' : 'disabled'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {sshEnabled
                    ? 'Enabled by ' + (policy?.enabled_by || 'unknown') +
                      (policy?.expires_at
                        ? ' - expires ' + new Date(policy.expires_at).toLocaleString()
                        : ' - no expiry')
                    : 'Domain controllers are reached through agents. These settings are inactive.'}
                </Typography>
              </Box>
            }
          />
          {!sshEnabled && (
            <TextField
              size="small"
              label="Reason (required to enable)"
              value={enableReason}
              onChange={(e) => setEnableReason(e.target.value)}
              helperText="At least 10 characters - it is recorded."
              sx={{ minWidth: 240 }}
            />
          )}
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {!isConfigured ? (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SSHIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    SSH Not Configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Configure SSH connection to manage your remote Samba4 AD/DC server.
                    This allows the Web UI to execute samba-tool commands remotely.
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenSetupDialog(false)}
                  >
                    Setup SSH Configuration
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SSHIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Current SSH Configuration</Typography>
                  <Chip
                    label="Configured"
                    color="success"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Server Host"
                      secondary={config?.host || 'N/A'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="SSH Port"
                      secondary={config?.port || 22}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="SSH Username"
                      secondary={config?.username || 'N/A'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Authentication Method"
                      secondary={
                        config?.auth_method === 'password'
                          ? 'Password Authentication'
                          : config?.auth_method === 'key'
                          ? 'SSH Key (no passphrase)'
                          : 'SSH Key (with passphrase)'
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Connection Timeout"
                      secondary={`${config?.connection_timeout || 30} seconds`}
                    />
                  </ListItem>
                  {config?.last_tested && (
                    <ListItem>
                      <ListItemText
                        primary="Last Tested"
                        secondary={new Date(config.last_tested).toLocaleString()}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={testing ? <CircularProgress size={20} /> : <TestIcon />}
                  onClick={handleTest}
                  disabled={testing}
                >
                  Test Connection
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete Configuration
                </Button>
              </CardActions>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About SSH Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>What is this?</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                SSH configuration allows this Web UI to connect to your remote Samba4 AD/DC
                server and execute samba-tool commands.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Credential Types:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li><strong>SSH Credentials</strong> - For connecting to the server (OS user)</li>
                  <li><strong>Sudo/Domain Credentials</strong> - For executing samba-tool commands (set via Sudo button)</li>
                  <li><strong>Web UI Login</strong> - For accessing this web interface</li>
                </ul>
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
                <strong>Security:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All credentials are encrypted using AES-256 and stored securely in the database.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Setup/Edit Dialog */}
      <Dialog open={setupDialogOpen} onClose={handleCloseSetupDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {config ? 'Edit SSH Configuration' : 'Setup SSH Configuration'}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              pt: 2,
              // Greyed out and inert while the fallback is off. pointerEvents
              // rather than a `disabled` prop on each control: it cannot miss a
              // field, and it keeps covering controls added later.
              ...(sshEnabled
                ? {}
                : { opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }),
            }}
            aria-disabled={!sshEnabled}
          >
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Important:</strong> These are the SSH credentials for connecting to the remote server.
                This is different from your domain administrator credentials (configured via the Sudo button).
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label="Server Hostname or IP Address"
              value={formData.host}
              onChange={(e) => setFormData({ ...formData, host: e.target.value })}
              margin="normal"
              required
              helperText="IP address or hostname of the Samba4 server"
            />
            <TextField
              fullWidth
              label="SSH Port"
              type="number"
              value={formData.port}
              onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="SSH Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              margin="normal"
              required
              helperText="Linux/Unix username on the remote server (e.g., tacit, ubuntu, admin)"
            />

            <FormControl component="fieldset" sx={{ mt: 3, mb: 2 }}>
              <FormLabel component="legend">Authentication Method</FormLabel>
              <RadioGroup
                value={formData.auth_method}
                onChange={(e) => setFormData({ ...formData, auth_method: e.target.value })}
              >
                <FormControlLabel
                  value="password"
                  control={<Radio />}
                  label="Password Authentication"
                />
                <FormControlLabel
                  value="key"
                  control={<Radio />}
                  label="SSH Key (no passphrase)"
                />
                <FormControlLabel
                  value="key_with_passphrase"
                  control={<Radio />}
                  label="SSH Key (with passphrase)"
                />
              </RadioGroup>
            </FormControl>

            {formData.auth_method === 'password' && (
              <TextField
                fullWidth
                label="SSH Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="normal"
                helperText={config ? "Leave blank to keep existing password" : "Password for SSH authentication"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {(formData.auth_method === 'key' || formData.auth_method === 'key_with_passphrase') && (
              <>
                <TextField
                  fullWidth
                  label="Private Key"
                  multiline
                  rows={8}
                  value={formData.private_key}
                  onChange={(e) => setFormData({ ...formData, private_key: e.target.value })}
                  margin="normal"
                  helperText={config ? "Leave blank to keep existing key" : "Paste your SSH private key"}
                />
                {formData.auth_method === 'key_with_passphrase' && (
                  <TextField
                    fullWidth
                    label="Passphrase (optional)"
                    type={showPassphrase ? 'text' : 'password'}
                    value={formData.passphrase}
                    onChange={(e) => setFormData({ ...formData, passphrase: e.target.value })}
                    margin="normal"
                    helperText="Passphrase for the private key"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle passphrase visibility"
                            onClick={() => setShowPassphrase(!showPassphrase)}
                            edge="end"
                          >
                            {showPassphrase ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </>
            )}

            <TextField
              fullWidth
              label="Connection Timeout (seconds)"
              type="number"
              value={formData.connection_timeout}
              onChange={(e) => setFormData({ ...formData, connection_timeout: parseInt(e.target.value) })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSetupDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (config ? 'Update' : 'Setup')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete SSH Configuration?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the SSH configuration? This will prevent the Web UI
            from managing your Samba4 server until you configure SSH again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SSHConfig;
